// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { GridColDef, GridToolbarContainer } from '@mui/x-data-grid';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { SyncButton } from '../../../components/Button/SyncButton';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import useBridgeUserAgreement from '../../../hooks/queries/bridge/agreement/useBridgeUserAgreement';
import { BurnAndAttestDialog } from '../withdrawals/BurnAndAttest';
import useBridgeHoldings from '../../../hooks/queries/bridge/useBridgeHoldings';
import { useParties } from '../../../contexts/PartiesContext';
import TemplateContract from '../../../utils/TemplateContract';
import { fmt, values } from '../../../utils/common';
import { aggregateUnlockedHoldings } from '../../../utils/aggregateHoldings';
import { DataTable } from '../../../components/Table/DataTable';

type Row = TemplateContract<Holding>;

const createHeaders: () => GridColDef<Row>[] = () =>
  {
    return [
      {
        field: 'instrument',
        headerName: 'Instrument',
        valueGetter: (params) => params.row.payload.instrument.id,
        flex: 100,
        filterable: false,
      },
      {
        field: 'holder',
        headerName: 'Holder',
        renderCell: (params) => <PartyDisplay party={params.row.payload.owner} />,
        flex: 150,
        filterable: false,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        valueGetter: (params) => fmt(params.row.payload.amount, 6),
        flex: 150,
        filterable: false,
      },
      {
        field: 'lockedBy',
        headerName: 'Locked By',
        renderCell: (params) =>
          params.row.payload.lock
            ? values(params.row.payload.lock.lockers).map((party) => (
                <PartyDisplay key={uuidv4()} party={party} />
              ))
            : null,
        flex: 150,
        filterable: false,
      },
      {
        field: 'lockedContext',
        headerName: 'Lock Context',
        valueGetter: (params) => (params.row.payload.lock ? params.row.payload.lock.context : ''),
        flex: 150,
        filterable: false,
      },
    ];
  };

 const Toolbar = () => {
   const [requestBurnIsOpen, setRequestBurnIsOpen] = useState(false);
   const holdings = useBridgeHoldings();
   const { party } = useParties();
   const isLoading = holdings.isLoading || !holdings.data;
   if (isLoading) return <Loading />;
   const userUnlockedHoldings = holdings.data.filter((holding) => holding.payload.owner === party && !holding.payload.lock);
   const hasHoldings = userUnlockedHoldings.length > 0;

   return (
     <GridToolbarContainer>
       {requestBurnIsOpen && (
         <BurnAndAttestDialog open={requestBurnIsOpen} onClose={() => setRequestBurnIsOpen(false)} holdings={userUnlockedHoldings} />
       )}

      <SyncButton onClick={() => setRequestBurnIsOpen(true)} disabled={!hasHoldings}>
         Withdraw
       </SyncButton>
     </GridToolbarContainer>
   );
 };

export const Holdings = () => {
  const bridgeUserAgreement = useBridgeUserAgreement();
  const holdings = useBridgeHoldings();
  const mergedHoldings = React.useMemo(
    () => aggregateUnlockedHoldings(holdings.data || []),
    [holdings.data],
  );
  const isLoading = bridgeUserAgreement.isLoading || holdings.isLoading;
  if (isLoading) return <Loading />;

  return (
    <DataTable
      title="Holdings"
      variant="h3"
      enableTableWrapper
      columns={createHeaders()}
      rows={mergedHoldings}
      rowKey={(a) => a.contractId}
      toolbar={Toolbar}
    />
  );
};
